import type { FullTradeCompanyNested, Incoterms } from 'api';
import {
  DateField,
  MultiCarrierAutocompleteField,
  PortAutocomplete,
  SiteAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  TradeCompanySiteAutocomplete,
  TradeCompanySiteSelector,
  useFormContext,
  useWatch,
} from 'form';
import { useContext, useEffect } from 'react';
import { SectionContainer } from 'ui';

import type { SalesOrderCreateFormValues } from '~/orders/pages/SalesOrderCreate/SalesOrderCreate';
import type { SalesOrderUpdateFormValues } from '~/orders/pages/SalesOrderUpdate/SalesOrderUpdate';

import { SalesOrderFormContext } from '../../contexts/SalesOrderFormContext';
import { BillTypeSelectField } from '../BillTypeSelectField/BillTypeSelectField';
import { TransportMethod } from '../TransportMethod/TransportMethod';

export const BuyerLogisticsSection = () => {
  const { incoterm, end_processor_id: endProcessor, purchaser_id: purchaser } = useWatch<SalesOrderCreateFormValues>();
  const { defaultValues, variant } = useContext(SalesOrderFormContext);
  const { resetField } = useFormContext();
  useEffect(() => {
    resetField('final_destination_site_id');
    resetField('delivery_site_id');
  }, [resetField, endProcessor, purchaser]);

  return (
    <SectionContainer title="Logistics">
      <TradeCompanySiteSelector
        name="final_destination_site_id"
        label="Final destination site"
        tradeCompanyId={endProcessor}
        required
        infoTooltip="Address where the material is processed, i.e. one of end processor's sites."
      />
      <DateField
        name="target_delivery_date"
        label="Target delivery date*"
        required
        infoTooltip="Target date by which the buyer expects all loads to be delivered by."
        fullWidth
      />
      <TextField
        name="max_number_of_shipments"
        label="Max number of shipments"
        infoTooltip="Maximum number of shipments the buyer expects to receive, i.e. all loads have to arrive in maximum this number of shipments. "
      />
      <MultiCarrierAutocompleteField
        name="allowed_shipping_lines_ids"
        label="Allowed shipping lines"
        infoTooltip="Carriers Safi can use to transport the material to end destination. If no carriers are selected, it is assumed any carrier is permitted."
      />
      <TransportMethod required />
      <TextField
        type="number"
        name="free_time_required"
        label="Free time required"
        infoTooltip="Number of “free” days required at destination, before detention & demurrage charges start to apply."
      />
      <IncotermSpecificFields
        incoterm={incoterm}
        purchaser={purchaser}
        defaultValues={defaultValues}
        endProcessor={endProcessor}
      />
      {variant === 'update' ? (
        <>
          <TradeCompanyAutocomplete name="shipper_id" label="Shipper" defaultValue={defaultValues?.shipper_id} />
          <TradeCompanyAutocomplete
            name="notify_party_id"
            label="Notify party"
            defaultValue={defaultValues?.notify_party_id}
          />
          <BillTypeSelectField name="bill_type" label="Bill type" />
          <TextField name="marks_and_numbers" label="Marks and numbers" />
          <TextField
            type="text"
            name="shipping_instruction_notes"
            label="Shipping instruction notes"
            minRows={2}
            multiline
          />
        </>
      ) : null}
    </SectionContainer>
  );
};

interface IncotermSpecificFieldsProps {
  incoterm: Incoterms | undefined;
  purchaser: FullTradeCompanyNested['id'] | undefined;
  endProcessor: FullTradeCompanyNested['id'] | undefined;
  defaultValues?: SalesOrderUpdateFormValues;
}

const IncotermSpecificFields = ({ incoterm, purchaser, endProcessor, defaultValues }: IncotermSpecificFieldsProps) => {
  switch (incoterm) {
    case 'EXW':
      return (
        <>
          <SiteAutocomplete
            name="collection_site_id"
            label="Collection site"
            required
            defaultValue={defaultValues?.collection_site_id}
            infoTooltip="Actual address of the loading site. Also, the address of where the inspection will take place."
          />
          <PortAutocomplete
            name="port_of_discharge_id"
            label="Port of discharge"
            defaultValue={defaultValues?.port_of_discharge_id}
            infoTooltip="Port of arrival, at which containers are unloaded from the vessel."
          />
          <PortAutocomplete
            name="icd_id"
            label="ICD"
            type="ICD"
            defaultValue={defaultValues?.icd_id}
            infoTooltip="Inland container depot. Containers are typically transported to this location via Train."
          />
        </>
      );
    case 'FOB':
      return (
        <>
          <PortAutocomplete
            name="port_of_loading_id"
            label="Port of loading"
            defaultValue={defaultValues?.port_of_loading_id}
            infoTooltip="Port of departure, at which containers are loaded on the vessel."
            required
          />
          <PortAutocomplete
            name="port_of_discharge_id"
            label="Port of discharge"
            defaultValue={defaultValues?.port_of_discharge_id}
            infoTooltip="Port of arrival, at which containers are unloaded from the vessel."
          />
          <PortAutocomplete
            name="icd_id"
            label="ICD"
            type="ICD"
            defaultValue={defaultValues?.icd_id}
            infoTooltip="Inland container depot. Containers are typically transported to this location via Train."
          />
        </>
      );
    case 'CFR':
    case 'CIF':
    case 'CPT':
    case 'CIP':
      return (
        <>
          <TradeCompanySiteAutocomplete
            name="delivery_site_id"
            label="Delivery site"
            tradeCompanies={[purchaser, endProcessor].filter((value) => value !== null && value !== undefined)}
            infoTooltip="Address where Safi is responsible for delivering the material to, as dictated by the Incoterm."
          />
          <PortAutocomplete
            name="port_of_discharge_id"
            label="Port of discharge"
            defaultValue={defaultValues?.port_of_discharge_id}
            required
            infoTooltip="Port of arrival, at which containers are unloaded from the vessel."
          />
          <PortAutocomplete
            name="icd_id"
            label="ICD"
            type="ICD"
            defaultValue={defaultValues?.icd_id}
            infoTooltip="Inland container depot. Containers are typically transported to this location via Train."
          />
        </>
      );
    case 'DAP':
    case 'DDP':
      return (
        <>
          <TradeCompanySiteAutocomplete
            name="delivery_site_id"
            label="Delivery site"
            required
            tradeCompanies={[purchaser, endProcessor].filter((value) => value !== null && value !== undefined)}
            defaultValue={defaultValues?.delivery_site_id ?? undefined}
            infoTooltip="Address where Safi is responsible for delivering the material to, as dictated by the Incoterm."
          />
          <PortAutocomplete
            name="port_of_discharge_id"
            label="Port of discharge"
            defaultValue={defaultValues?.port_of_discharge_id}
            infoTooltip="Port of arrival, at which containers are unloaded from the vessel."
          />
          <PortAutocomplete
            name="icd_id"
            label="ICD"
            type="ICD"
            defaultValue={defaultValues?.icd_id}
            infoTooltip="Inland container depot. Containers are typically transported to this location via Train."
          />
        </>
      );
    default:
      return null;
  }
};
